import React, { useMemo, useState } from "react";
import { useIsFetching, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import {
    fetchGuests,
    fetchInsights,
    fetchOverview,
    fetchStaff,
    fetchSummary,
    fetchUpsell,
} from "../../api/analytics";
import {
    LaunchingSoon,
    PALETTE,
    formatCompactCurrency,
    formatMinutes,
    formatPercent,
} from "./Analytics/Primitives";
import OverviewTab from "./Analytics/OverviewTab";
import StaffTab from "./Analytics/StaffTab";
import UpsellTab from "./Analytics/UpsellTab";
import GuestsTab from "./Analytics/GuestsTab";
import WeeklyTab from "./Analytics/WeeklyTab";

const TABS = [
    { id: "overview", label: "Overview" },
    { id: "employees", label: "Employee Performance" },
    { id: "complaints", label: "Complaints" },
    { id: "upsell", label: "Upsell Funnel" },
    { id: "partners", label: "Partners" },
    { id: "guests", label: "Guest Profiles" },
    { id: "weekly", label: "Weekly Report" },
    { id: "compare", label: "Properties Comparison" },
];

const startOfWeek = (value) => {
    const date = dayjs(value);
    return date.subtract((date.day() + 6) % 7, "day").startOf("day");
};

const defaultGranularity = (from, to) => {
    const days = dayjs(to).diff(dayjs(from), "day") + 1;
    if (days <= 31) return "day";
    if (days <= 182) return "week";
    return "month";
};

const inputStyle = {
    padding: "7px 10px",
    border: "1.5px solid #E4E7EF",
    borderRadius: 9,
    fontFamily: "inherit",
    fontSize: 12,
    color: "#1a1a2e",
    background: "#F9FAFB",
    outline: "none",
};

export default function Analytics() {
    const hotelId = localStorage.getItem("hotelId");
    const queryClient = useQueryClient();
    const refreshing = useIsFetching({ queryKey: ["analytics"] }) > 0;

    const [range, setRange] = useState(() => ({
        from: dayjs().startOf("month").format("YYYY-MM-DD"),
        to: dayjs().format("YYYY-MM-DD"),
    }));
    const [granularityOverride, setGranularityOverride] = useState("");
    const [tab, setTab] = useState("overview");
    const [weekOffset, setWeekOffset] = useState(0);

    const granularity = granularityOverride || defaultGranularity(range.from, range.to);

    const rangeValid = useMemo(
        () =>
            dayjs(range.from).isValid() &&
            dayjs(range.to).isValid() &&
            !dayjs(range.to).isBefore(dayjs(range.from)),
        [range],
    );

    const enabled = Boolean(hotelId) && rangeValid;

    const summaryQuery = useQuery({
        queryKey: ["analytics", "summary", hotelId, range.from, range.to],
        queryFn: () => fetchSummary(hotelId, range),
        enabled,
    });

    const overviewQuery = useQuery({
        queryKey: ["analytics", "overview", hotelId, range.from, range.to, granularity],
        queryFn: () => fetchOverview(hotelId, range, granularity),
        enabled: enabled && tab === "overview",
    });

    const staffQuery = useQuery({
        queryKey: ["analytics", "staff", hotelId, range.from, range.to],
        queryFn: () => fetchStaff(hotelId, range),
        enabled: enabled && tab === "employees",
    });

    const upsellQuery = useQuery({
        queryKey: ["analytics", "upsell", hotelId, range.from, range.to],
        queryFn: () => fetchUpsell(hotelId, range),
        enabled: enabled && tab === "upsell",
    });

    const guestsQuery = useQuery({
        queryKey: ["analytics", "guests", hotelId, range.from, range.to],
        queryFn: () => fetchGuests(hotelId, range),
        enabled: enabled && tab === "guests",
    });

    const weekParam = useMemo(
        () => startOfWeek(dayjs()).add((weekOffset - 1) * 7, "day").format("YYYY-MM-DD"),
        [weekOffset],
    );

    const insightsQuery = useQuery({
        queryKey: ["analytics", "insights", hotelId, weekParam],
        queryFn: () => fetchInsights(hotelId, weekParam),
        enabled: Boolean(hotelId) && tab === "weekly",
    });

    const summary = summaryQuery.data;
    const lastUpdated = summaryQuery.dataUpdatedAt
        ? dayjs(summaryQuery.dataUpdatedAt).format("HH:mm:ss")
        : null;

    const refresh = () => queryClient.invalidateQueries({ queryKey: ["analytics"] });

    const kpis = [
        {
            label: "Booked Rooms",
            value: summary?.occupancy?.booked_rooms ?? 0,
            sub: `${formatPercent(summary?.occupancy?.rate)} occupancy`,
            color: PALETTE.blue,
        },
        {
            label: "Alfred Usage",
            value: formatPercent(summary?.engagement?.usage_rate),
            sub: `${summary?.engagement?.engaged_guests ?? 0} of ${
                summary?.engagement?.staying_guests ?? 0
            } guests`,
            color: PALETTE.purple,
        },
        {
            label: "Total Revenue",
            value: formatCompactCurrency(summary?.total_revenue),
            sub: "Driven by Alfred",
            color: PALETTE.green,
        },
        {
            label: "Total Requests",
            value: summary?.requests?.total ?? 0,
            sub: `${summary?.requests?.missed ?? 0} declined`,
            color: "#1a1a2e",
        },
        {
            label: "Revenue Lost",
            value: formatCompactCurrency(summary?.revenue_lost),
            sub: "From declined requests",
            color: PALETTE.red,
        },
        {
            label: "Avg Closure",
            value: summary?.avg_closure_minutes > 0 ? formatMinutes(summary.avg_closure_minutes) : "—",
            sub: "Across all request types",
            color: PALETTE.amber,
        },
    ];

    const activeQuery = {
        overview: overviewQuery,
        employees: staffQuery,
        upsell: upsellQuery,
        guests: guestsQuery,
        weekly: insightsQuery,
    }[tab];

    const renderTab = () => {
        if (!rangeValid) {
            return <Notice message="Select a valid date range to see analytics." />;
        }
        if (activeQuery?.isLoading) {
            return <Notice message="Loading…" />;
        }
        if (activeQuery?.isError) {
            return <Notice message="Could not load this report. Try again." tone="error" />;
        }

        switch (tab) {
            case "overview":
                return (
                    <OverviewTab
                        summary={overviewQuery.data?.summary}
                        requests={overviewQuery.data?.requests}
                        trends={overviewQuery.data?.trends}
                        granularity={granularity}
                    />
                );
            case "employees":
                return <StaffTab staff={staffQuery.data} />;
            case "upsell":
                return <UpsellTab upsell={upsellQuery.data} />;
            case "guests":
                return <GuestsTab guests={guestsQuery.data} />;
            case "weekly":
                return (
                    <WeeklyTab
                        insights={insightsQuery.data}
                        weekOffset={weekOffset}
                        onWeekChange={setWeekOffset}
                    />
                );
            default:
                return <LaunchingSoon />;
        }
    };

    return (
        <div
            style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
                background: "#F8F9FC",
                borderRadius: 12,
                overflow: "hidden",
                height: "100%",
            }}
        >
            <div
                style={{
                    padding: "16px 24px 0",
                    flex: "0 0 auto",
                    background: "#fff",
                    borderBottom: "1px solid #F0F2F7",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: 14,
                        gap: 16,
                    }}
                >
                    <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#1a1a2e", letterSpacing: 2 }}>
                        ANALYTICS
                    </h1>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        {lastUpdated && (
                            <span style={{ fontSize: 10.5, color: "#9097ac" }}>
                                Updated {lastUpdated}
                            </span>
                        )}
                        <button
                            type="button"
                            onClick={refresh}
                            disabled={refreshing}
                            title="Refresh analytics"
                            aria-label="Refresh analytics"
                            style={{
                                ...inputStyle,
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                cursor: refreshing ? "default" : "pointer",
                                color: refreshing ? "#9097ac" : "#1a1a2e",
                            }}
                        >
                            <svg
                                className={refreshing ? "animate-spin" : undefined}
                                width="13"
                                height="13"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.4"
                                strokeLinecap="round"
                            >
                                <path d="M21 12a9 9 0 1 1-2.64-6.36" />
                                <path d="M21 3v6h-6" />
                            </svg>
                            {refreshing ? "Refreshing" : "Refresh"}
                        </button>
                        <select
                            value={granularity}
                            onChange={(event) => setGranularityOverride(event.target.value)}
                            style={{ ...inputStyle, cursor: "pointer" }}
                        >
                            <option value="day">Daily</option>
                            <option value="week">Weekly</option>
                            <option value="month">Monthly</option>
                        </select>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <input
                                type="date"
                                value={range.from}
                                max={range.to}
                                onChange={(event) =>
                                    setRange((current) => ({ ...current, from: event.target.value }))
                                }
                                style={inputStyle}
                            />
                            <span style={{ fontSize: 11, color: "#9097ac" }}>–</span>
                            <input
                                type="date"
                                value={range.to}
                                min={range.from}
                                onChange={(event) =>
                                    setRange((current) => ({ ...current, to: event.target.value }))
                                }
                                style={inputStyle}
                            />
                        </div>
                    </div>
                </div>

                <div style={{ display: "flex" }}>
                    {kpis.map((kpi, index) => (
                        <React.Fragment key={kpi.label}>
                            <div style={{ flex: 1, padding: "4px 14px 12px", minWidth: 0 }}>
                                <div
                                    style={{
                                        fontSize: 9.5,
                                        fontWeight: 700,
                                        color: "#9097ac",
                                        letterSpacing: 0.5,
                                        marginBottom: 4,
                                    }}
                                >
                                    {kpi.label.toUpperCase()}
                                </div>
                                <div style={{ fontSize: 21, fontWeight: 700, color: kpi.color, lineHeight: 1.1 }}>
                                    {summaryQuery.isLoading ? "…" : kpi.value}
                                </div>
                                <div style={{ fontSize: 10.5, color: "#9097ac", marginTop: 2 }}>{kpi.sub}</div>
                            </div>
                            {index < kpis.length - 1 && (
                                <div style={{ width: 1, background: "#F0F2F7", margin: "4px 0 8px" }} />
                            )}
                        </React.Fragment>
                    ))}
                </div>

                <div style={{ display: "flex", gap: 2, overflowX: "auto" }}>
                    {TABS.map((entry) => (
                        <button
                            key={entry.id}
                            onClick={() => setTab(entry.id)}
                            style={{
                                padding: "10px 15px 9px",
                                border: "none",
                                background: "transparent",
                                fontFamily: "inherit",
                                fontSize: 12.5,
                                fontWeight: tab === entry.id ? 700 : 500,
                                color: tab === entry.id ? PALETTE.accent : "#69708A",
                                borderBottom: `2px solid ${tab === entry.id ? PALETTE.accent : "transparent"}`,
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {entry.label}
                        </button>
                    ))}
                </div>
            </div>

            <div
                style={{
                    flex: 1,
                    overflowY: "auto",
                    padding: "18px 24px 28px",
                    opacity: refreshing ? 0.55 : 1,
                    transition: "opacity 140ms ease",
                }}
            >
                {renderTab()}
            </div>
        </div>
    );
}

function Notice({ message, tone }) {
    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 260,
                fontSize: 13,
                color: tone === "error" ? PALETTE.accent : "#9097ac",
            }}
        >
            {message}
        </div>
    );
}
